import React, { useMemo } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Stack, Tooltip, Typography } from '@mui/material';

import { SupplierRoom } from '~/services/AccommodationService';

import { getExtractedInclusions } from '~/utils/getExtractedInclusions';
import { titleCase } from '~/utils/stringUtils';

interface Props {
  supplierRoom: SupplierRoom;
  showNameInTitle?: boolean;
}

export default function SupplierRoomListElement({ supplierRoom, showNameInTitle }: Props) {
  const title = useMemo(() => {
    if (showNameInTitle) {
      return `${titleCase(supplierRoom.supplier)} - ${supplierRoom.name} - ${supplierRoom.supplierId}`;
    }
    return `${titleCase(supplierRoom.supplier)} - ${supplierRoom.supplierId}`;
  }, [showNameInTitle, supplierRoom]);

  const extraInclusions = useMemo(() => {
    const seen = new Set<string>();
    return getExtractedInclusions(supplierRoom).filter(({ code }) => {
      if (seen.has(code)) return false;
      seen.add(code);
      return true;
    });
  }, [supplierRoom.inclusions]);

  return (
    <Accordion
      key={supplierRoom.id}
      defaultExpanded={false}
      sx={{
        borderColor: 'grey.400',
        borderWidth: '2px',
        borderStyle: 'solid',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" gap={1}>
          <span>{title}</span>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack direction="column" key={supplierRoom.id}>
          <Box>
            <Typography>
              <strong>Name:</strong> {supplierRoom.name}
            </Typography>
            <Typography>
              <strong>Supplier:</strong> {titleCase(supplierRoom.supplier)}
            </Typography>
            <Typography>
              <strong>Supplier ID:</strong> {supplierRoom.supplierId}
            </Typography>
          </Box>
          {extraInclusions.length > 0 && (
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
              <Typography component="span">
                <strong>Inclusions:</strong>
              </Typography>
              <Tooltip title="Inclusions extracted from the room name">
                <InfoOutlined sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
              {extraInclusions.map(({ code, label }) => (
                <Chip key={code} label={label} color="info" size="small" />
              ))}
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
